// src/core/hooks/useUrlFilters.ts

import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

export type UrlFilterFieldType =
  | "numberArray"
  | "stringArray"
  | "string"
  | "number"

export type UrlFilterSchema = Record<string, UrlFilterFieldType>

type FieldTypeToValue = {
  numberArray: number[]
  stringArray: string[]
  string: string
  number: number
}

/** The typed, partial filters object derived from a schema. */
export type FiltersOf<S extends UrlFilterSchema> = {
  [K in keyof S]?: FieldTypeToValue[S[K]]
}

const parseValue = (
  type: UrlFilterFieldType,
  raw: string,
): number[] | string[] | string | number | undefined => {
  switch (type) {
    case "numberArray": {
      const arr = raw
        .split(",")
        .map(Number)
        .filter((n) => Number.isFinite(n))
      return arr.length ? arr : undefined
    }
    case "stringArray": {
      const arr = raw.split(",").filter(Boolean)
      return arr.length ? arr : undefined
    }
    case "string":
      return raw || undefined
    case "number": {
      const n = Number(raw)
      return Number.isFinite(n) ? n : undefined
    }
  }
}

const serializeValue = (
  type: UrlFilterFieldType,
  value: unknown,
): string | null => {
  if (value == null) return null
  switch (type) {
    case "numberArray":
    case "stringArray":
      return Array.isArray(value) && value.length ? value.join(",") : null
    case "string":
      return value === "" ? null : String(value)
    case "number":
      return String(value)
  }
}

/**
 * Syncs a flat object of primitive filter values with the URL query string so
 * the active filters can be shared via a link.
 *
 * The hook only handles serialization of primitives (arrays of ids/names,
 * strings, dates-as-strings, numbers). Mapping those primitives back to richer
 * form objects (e.g. autocomplete options) stays with the caller, since it
 * usually depends on asynchronously loaded option lists.
 *
 * Declare the schema as a module-level constant (`as const satisfies
 * UrlFilterSchema`) so its identity is stable across renders.
 */
export function useUrlFilters<S extends UrlFilterSchema>(schema: S) {
  const [searchParams, setSearchParams] = useSearchParams()

  const read = useCallback((): FiltersOf<S> => {
    const result: Partial<Record<keyof S, unknown>> = {}
    ;(Object.keys(schema) as (keyof S)[]).forEach((key) => {
      const raw = searchParams.get(key as string)
      if (raw == null) return
      const parsed = parseValue(schema[key], raw)
      if (parsed !== undefined) result[key] = parsed
    })
    return result as FiltersOf<S>
  }, [searchParams, schema])

  const hasUrlFilters = useMemo(
    () => Object.keys(schema).some((key) => searchParams.has(key)),
    [searchParams, schema],
  )

  const write = useCallback(
    (filters: FiltersOf<S>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          ;(Object.keys(schema) as (keyof S)[]).forEach((key) => {
            params.delete(key as string)
            const serialized = serializeValue(schema[key], filters[key])
            if (serialized != null) params.set(key as string, serialized)
          })
          return params
        },
        { replace: true },
      )
    },
    [schema, setSearchParams],
  )

  return { read, write, hasUrlFilters }
}
